import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { Duck } from "@/interfaces/duck";
import { useDemoCruiseStore } from "./demoCruiseStore";

export const useDemoDuckStore = defineStore('demoDuck', () => {
    const ducks = ref<Array<Duck>>([]);
    const currentDuck = ref<Duck | null>(null);

    const getDuckById = computed(() => (id: string) => {
        return ducks.value.find(d => String(d.id) === id);
    });

    const getTotalDucks = computed(() => {
        return ducks.value.length;
    });

    const getFoundDucks = computed(() => {
        return ducks.value.filter(d => d.found > 0).length;
    });

    const getRemainingDucks = computed(() => {
        return getTotalDucks.value - getFoundDucks.value;
    });

    const getDucksOrderedById = computed(() => {
        return [...ducks.value].sort((a, b) => {
            if (a.found !== b.found) {
                return a.found ? -1 : 1;
            }
            return a.id - b.id;
        });
    });

    // Initialize with ducks from cruise store
    const initializeDucks = () => {
        const cruiseStore = useDemoCruiseStore();
        ducks.value = [];

        // Flatten all ducks from all cruises
        cruiseStore.cruises.forEach(cruise => {
            cruise.ducks.forEach(duck => {
                // Assign rarity based on duck ID for demo purposes
                const rarity = assignDemoRarity(Number(duck.id));
                
                ducks.value.push({
                    id: Number(duck.id),
                    found: duck.found,
                    uuid: duck.uuid,
                    idCruise: cruise.id,
                    image: duck.image || '/ducksImages/duckIcon.webp',
                    rarity: rarity
                });
            });
        });
    };

    // Helper function to assign demo rarity based on duck ID (for demo purposes only)
    const assignDemoRarity = (duckId: number): 'COMMON' | 'RARE' | 'LEGENDARY' => {
        // Use modulo to distribute rarities consistently:
        // Common: 60% (0-5), Rare: 30% (6-8), Legendary: 10% (9)
        const mod = duckId % 10;
        if (mod <= 5) return 'COMMON';
        if (mod <= 8) return 'RARE';
        return 'LEGENDARY';
    };

    // Set the current duck by ID
    const setCurrentDuck = (id: string | number) => {
        // If ducks are empty, initialize
        if (ducks.value.length === 0) {
            initializeDucks();
        }

        // Find duck by ID (string or number)
        const duck = ducks.value.find(d => String(d.id) === String(id));
        if (duck) {
            currentDuck.value = duck;
            return true;
        }
        return false;
    };

    // Find all ducks in a specific cruise
    const getDucksByCruiseId = (cruiseId: number) => {
        return ducks.value.filter(duck => duck.idCruise === cruiseId);
    };

    // Send a message (simulated)
    const sendMessage = async (message: string, duckId: string) => {
        console.log(`Demo: Sending message to duck ${duckId}: ${message}`);
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        return { success: true };
    };

    // Hide a duck (simulated)
    const hideDuck = async (duckId: string) => {
        console.log(`Demo: Hiding duck ${duckId}`);
        // Find the duck and update its status
        const duck = ducks.value.find(d => String(d.uuid) === duckId);
        if (duck) {
            duck.found = 0;
            // If this is the current duck, update it
            if (currentDuck.value && currentDuck.value.uuid === duckId) {
                currentDuck.value = { ...duck };
            }
        }
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        return { success: true };
    };

    return { 
        ducks,
        currentDuck,
        getDuckById, 
        getTotalDucks,
        getFoundDucks,
        getRemainingDucks,
        getDucksOrderedById,
        initializeDucks,
        setCurrentDuck,
        getDucksByCruiseId,
        sendMessage,
        hideDuck,
        assignDemoRarity
    };
});